<template>
  <transition name="dialog-fade">
    <div v-if="show" class="dialog-overlay" @click="handleOverlayClick">
      <div class="dialog-card" :style="{ maxWidth: width }" @click.stop>
        <!-- Header -->
        <div
          class="dialog-header"
          :style="{
            background: theme.background,
            color: theme.color,
          }"
        >
          <font-awesome-icon
            class="dialog-icon"
            v-if="theme.icon != ''"
            :icon="['fas', theme.icon]"
          />

          <div>
            <h4 class="mb-0">{{ title }}</h4>
          </div>
        </div>

        <!-- Body -->
        <div class="dialog-body">
          <p v-if="message" class="dialog-message">
            {{ message }}
          </p>

          <slot></slot>
        </div>

        <!-- Footer -->
        <div class="dialog-footer">
          <b-button
            v-if="cancelText"
            variant="outline-secondary"
            class="btn btn-size2"
            @click="$emit('cancel')"
          >
            {{ cancelText }}
          </b-button>

          <b-button
            class="btn btn-size2"
            :variant="theme.button"
            @click="$emit('confirm')"
          >
            {{ confirmText }}
          </b-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "AlertDialog",

  props: {
    show: {
      type: Boolean,
      default: false,
    },

    title: {
      type: String,
      default: "",
    },

    message: {
      type: String,
      default: "",
    },

    type: {
      type: String,
      default: "info",
    },

    width: {
      type: String,
      default: "500px",
    },

    confirmText: {
      type: String,
      default: "OK",
    },

    cancelText: {
      type: String,
      default: "",
    },

    persistent: {
      type: Boolean,
      default: true,
    },

    closeOnEsc: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    theme() {
      const themes = {
        info: {
          color: "#0d6efd",
          background: "#eaf3ff",
          icon: "circle-info",
          button: "primary",
        },

        success: {
          color: "#198754",
          background: "#eaf8ef",
          icon: "circle-check",
          button: "success",
        },

        warning: {
          color: "#d97706",
          background: "#fff6e5",
          icon: "triangle-exclamation",
          button: "warning",
        },

        danger: {
          color: "#dc3545",
          background: "#fdecec",
          icon: "circle-xmark",
          button: "danger",
        },
      };

      return themes[this.type] || themes.info;
    },
  },

  mounted() {
    window.addEventListener("keydown", this.handleEsc);
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.handleEsc);
  },

  methods: {
    handleOverlayClick() {
      if (!this.persistent) {
        this.$emit("cancel");
      }
    },

    handleEsc(e) {
      if (e.key !== "Escape") return;

      if (!this.closeOnEsc) return;

      if (!this.persistent) {
        this.$emit("cancel");
      }
    },
  },
};
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  inset: 0;

  background: rgba(0, 0, 0, 0.55);

  display: flex;
  justify-content: center;
  align-items: center;

  z-index: 999999;

  padding: 20px;
}

.dialog-card {
  width: 100%;
  background: white;

  border-radius: 14px;

  overflow: hidden;

  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.dialog-header {
  display: flex;

  align-items: center;

  padding: 20px;
}

.dialog-icon {
  font-size: 34px;

  margin-right: 15px;
}

.dialog-body {
  padding: 25px;
}

.dialog-message {
  margin-bottom: 0;

  color: #555;

  line-height: 1.6;
}

.dialog-footer {
  padding: 18px 25px;

  border-top: 1px solid #eee;

  display: flex;

  justify-content: flex-end;

  gap: 10px;
}

.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.25s;
}

.dialog-fade-enter,
.dialog-fade-leave-to {
  opacity: 0;
}
</style>
